export const getPublicIP = async (): Promise<string | null> => {
  try {
    const res = await fetch('https://ipapi.co/json/');
    const data = await res.json();
    return data.ip;
  } catch (err) {
    console.error('Failed to fetch IP address:', err);
    return null;
  }
};

export const getUserIP = async (): Promise<string | null> => {
  const userIP = await getPublicIP();
  // const allowedIPs = ['54.227.44.17','139.84.144.218','107.22.40.80','3.6.118.61','180.151.48.130','106.51.0.250'];
  const allowedIPs = process.env.REACT_APP_Chennai_IP_Address
    ? process.env.REACT_APP_Chennai_IP_Address.split(',')
    : [];
  const location = userIP && allowedIPs.includes(userIP) ? 'CHN Office' : 'Others';
  // console.log(allowedIPs,"allowedIps", userIP," userIP", location, "location", allowedIPs.includes(userIP),"testing");

  return location;
}

export const getDeviceType = () => {
  const ua = navigator.userAgent;

  if (/mobile/i.test(ua)) {
    return "Mobile";
  }

  if (/tablet/i.test(ua)) {
    return "Tablet";
  }

  if (/iPad|Android 3.0|xoom|sch-i800|playbook|tablet|kindle/i.test(ua)) {
    return "Tablet";
  }

  if (/Macintosh|Windows NT|Linux x86_64|X11/i.test(ua)) {
    return "Desktop";
  }

  return "Unknown";
};

import { format, isValid, parseISO } from 'date-fns';

export const cleanObject = (obj: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => {
      if (v == null) return false; // skip null/undefined
      if (Array.isArray(v)) return v.length > 0; // skip empty arrays
      if (typeof v === "string") return v.trim() !== ""; // skip empty strings
      return true; // keep numbers, objects, etc.
    })
  );

// ============================================================================
// Date Formatting Utilities
// ============================================================================

type DateInput = Date | string | null | undefined;

/**
 * Format date for HTML date inputs.
 * Always returns YYYY-MM-DD format (required by <input type="date">).
 * 
 * Handles:
 * - Date objects
 * - ISO strings (2025-01-06T00:00:00.000Z)
 * - Already formatted strings (2025-01-06) - passthrough
 * - null/undefined - returns empty string
 * 
 * @example
 * formatDateForInput(new Date()) // "2025-01-06"
 * formatDateForInput("2025-01-06T10:30:00Z") // "2025-01-06"
 * formatDateForInput("2025-01-06") // "2025-01-06" (passthrough)
 */
export const formatDateForInput = (dateValue: DateInput): string => {
  if (!dateValue) return '';

  // Passthrough: already in YYYY-MM-DD format
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }

  // Parse and format
  let date: Date;
  if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    // Try ISO parse first
    date = parseISO(dateValue);
    if (!isValid(date)) {
      // Fallback to Date constructor
      date = new Date(dateValue);
    }
  }

  if (!isValid(date)) {
    return '';
  }

  return format(date, 'yyyy-MM-dd');
};

/**
 * Format date for display purposes.
 * Uses the provided format string (from org config) or defaults to 'dd-MM-yyyy'.
 * 
 * @param dateValue - Date to format
 * @param displayFormat - date-fns format string (e.g., 'dd-MM-yyyy', 'dd/MM/yyyy')
 * 
 * @example
 * const { dateFormat } = useOrgConfig();
 * formatDateForDisplay(record.createdAt, dateFormat) // "06-01-2025"
 */
export const formatDateForDisplay = (
  dateValue: DateInput,
  displayFormat: string = 'dd-MM-yyyy'
): string => {
  if (!dateValue) return '';

  // Parse the date
  let date: Date;
  if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = parseISO(dateValue);
    if (!isValid(date)) {
      date = new Date(dateValue);
    }
  }

  if (!isValid(date)) {
    return '';
  }

  return format(date, displayFormat);
};

/**
 * Calculate elapsed time (years/months, or days if under a month) from a
 * joining date to a reference date. Shared by the resignation screens
 * (Approve/Edit had their own identical copies — consolidated here so the
 * Resignations list "Year Of Service" column stays consistent with the
 * detail pages).
 *
 * `compact` renders "4y 2m" instead of "4 years, 2 months" — used by the
 * Resignations list column, which is tight on horizontal space; the detail/
 * approve/edit pages keep the spelled-out form by leaving this false.
 *
 * `endDate` (MR !2027 review, owner decision): tenure must stop growing once
 * someone has actually left — without this, "Year Of Service" for a former
 * employee would keep counting up every month forever. Pass the
 * resignation's relieving date here; a missing or still-future relieving
 * date (still in notice) falls back to counting to today.
 */
export const calculateExperience = (
  joiningDate: string | null | undefined,
  compact: boolean = false,
  endDate?: string | null
): string => {
  if (!joiningDate) return 'N/A';

  // parseISO (not `new Date(...)`) — a bare 'YYYY-MM-DD' string has no time
  // zone, and the native Date constructor treats that as UTC midnight while
  // everything below reads it back out in local time via getFullYear/getMonth/
  // getDate. parseISO interprets a date-only ISO string as local time instead,
  // so a viewer west of UTC doesn't get tenure math skewed by up to a day.
  const joinDate = parseISO(joiningDate);

  if (!isValid(joinDate)) {
    return 'N/A';
  }

  const today = new Date();

  // referenceDate is the relieving date, unless it's missing, unparseable,
  // or still in the future — in all three cases we count to today instead.
  let referenceDate = today;
  if (endDate) {
    const parsedEnd = parseISO(endDate);
    if (isValid(parsedEnd) && parsedEnd.getTime() <= today.getTime()) {
      referenceDate = parsedEnd;
    }
  }

  // A joining date after the reference date (bad data entry, or a DOJ that
  // post-dates the relieving date) would otherwise fall through every branch
  // below to a negative daysDiff — render N/A instead of a nonsensical
  // "-365 days".
  if (joinDate.getTime() > referenceDate.getTime()) {
    return 'N/A';
  }

  // Calculate difference in years
  let years = referenceDate.getFullYear() - joinDate.getFullYear();

  // Adjust for months and days
  if (
    referenceDate.getMonth() < joinDate.getMonth() ||
    (referenceDate.getMonth() === joinDate.getMonth() && referenceDate.getDate() < joinDate.getDate())
  ) {
    years--;
  }

  // Calculate remaining months
  let months = referenceDate.getMonth() - joinDate.getMonth();
  if (months < 0) months += 12;

  // Adjust for days
  if (referenceDate.getDate() < joinDate.getDate()) {
    months--;
    if (months < 0) months += 12;
  }

  // Format the result
  if (compact) {
    if (years > 0 && months > 0) {
      return `${years}y, ${months}m`;
    } else if (years > 0) {
      return `${years}y`;
    } else if (months > 0) {
      return `${months}m`;
    } else {
      const daysDiff = Math.floor((referenceDate.getTime() - joinDate.getTime()) / (1000 * 60 * 60 * 24));
      return `${daysDiff}d`;
    }
  }

  if (years > 0 && months > 0) {
    return `${years} year${years > 1 ? 's' : ''}, ${months} month${months > 1 ? 's' : ''}`;
  } else if (years > 0) {
    return `${years} year${years > 1 ? 's' : ''}`;
  } else if (months > 0) {
    return `${months} month${months > 1 ? 's' : ''}`;
  } else {
    // If less than a month
    const daysDiff = Math.floor((referenceDate.getTime() - joinDate.getTime()) / (1000 * 60 * 60 * 24));
    return `${daysDiff} day${daysDiff > 1 ? 's' : ''}`;
  }
};

/**
 * Resignation status values are a mixed bag in the backend enum — most are
 * already title case ("Approved", "Resignation Withdrawn") but PENDING and
 * CANCELLED are stored all-caps. Fixing this at the source would mean a data
 * migration plus touching 20+ string-equality checks against the Go
 * constants, so it's normalized here for display only.
 */
const RESIGNATION_STATUS_DISPLAY_OVERRIDES: Record<string, string> = {
  PENDING: 'Pending',
  CANCELLED: 'Cancelled'
};

export const formatResignationStatus = (status: string | null | undefined): string => {
  if (!status) return 'N/A';
  return RESIGNATION_STATUS_DISPLAY_OVERRIDES[status] ?? status;
};